/**
 * ListeOlustur — bir posta listesi kurmak ya da listeye üye eklemek (backlog #585, backend POST /email/lists ve POST /email/lists/{id}/members).
 *
 * Kullanıcılar listesinin KENDİ süzgeci (arama + Filtreler paneli: rol, durum, giriş, fuar, firma, firma uyruğu / türü, son giriş): sunucu kümeyi aynı SQL ile
 * kendisi kurar, bu form kimlik listesi göndermez. Süzgeci değiştirdikçe (yarım saniye sonra) kaç kişi bulunduğu gösterilir; kayıtla yazılır. Süzgeçsiz
 * bırakılırsa TÜM kullanıcılar bulunur ve sayı bunu açıkça söyler. "Boş liste" süzgeçsiz, üyesiz bir liste kurar (üyeler sonra eklenir).
 * `liste` verilmişse yeni liste yok, o listeye ekleme vardır.
 *
 * `isOpen` kapalıyken hiçbir şey çizilmez: her açılışta form sıfırdan başlar.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import KullaniciFiltreleri from "../../../../../Components/Kullanici/KullaniciFiltreleri";
import { BOS_FILTRE, aktifFiltreSayisi, topluAktivasyonParametreleri } from "../../../../../Components/Kullanici/kullaniciListe";
import { getUserGroups } from "../../../../../services/userService";
import { fuarService } from "../../../../../services/fuarService";
import { addMailListMembers, createMailList, previewUserFilter, suzgecGovdesi } from "../../../../../services/postaListeleriService";

const ONIZLEME_BEKLEME_MS = 500;

const OlusturForm = ({ toggle, liste, onDone }) => {
    const { t } = useTranslation("moduller");
    const ekleme = Boolean(liste);
    const [ad, setAd] = useState("");
    const [not, setNot] = useState("");
    const [bos, setBos] = useState(false);
    const [arama, setArama] = useState("");
    const [filtre, setFiltre] = useState(BOS_FILTRE);
    const [roller, setRoller] = useState([]);
    const [fuarlar, setFuarlar] = useState([]);
    const [sonuc, setSonuc] = useState(null);                 // { anahtar, veri | hata }
    const [kaydediyor, setKaydediyor] = useState(false);
    const [hata, setHata] = useState(null);

    // Süzgeç seçicileri (rol, fuar): bir kez, hata sessiz — seçici boş kalır, form yine çalışır.
    useEffect(() => {
        let ignore = false;
        async function run() {
            try {
                const g = await getUserGroups();
                if (!ignore) setRoller((g || []).map((x) => ({ id: x.id, ad: x.userGroupName })));
            } catch {
                if (!ignore) setRoller([]);
            }
            try {
                const f = await fuarService.getSelectableFuarList();
                if (!ignore) setFuarlar((f || []).map((x) => ({ fuarId: x.fuarId, ad: x.fuarName || x.fuarShortName || `#${x.fuarId}` })));
            } catch {
                if (!ignore) setFuarlar([]);
            }
        }
        run();
        return () => { ignore = true; };
    }, []);

    const parametreler = useMemo(() => suzgecGovdesi(topluAktivasyonParametreleri({ arama: arama.trim(), filtre })), [arama, filtre]);
    const anahtar = bos ? null : JSON.stringify(parametreler);
    const suzgecYok = Object.keys(parametreler).length === 0;

    useEffect(() => {
        if (anahtar === null) return undefined;
        let ignore = false;
        const zamanlayici = setTimeout(async () => {
            try {
                const veri = await previewUserFilter(JSON.parse(anahtar));
                if (!ignore) setSonuc({ anahtar, veri });
            } catch (e) {
                if (!ignore) setSonuc({ anahtar, hata: e.message });
            }
        }, ONIZLEME_BEKLEME_MS);
        return () => { ignore = true; clearTimeout(zamanlayici); };
    }, [anahtar]);
    const buSonuc = sonuc && sonuc.anahtar === anahtar ? sonuc : null;
    const bulunan = buSonuc?.veri?.matched ?? 0;

    const kaydedebilir = !kaydediyor && (ekleme || ad.trim() !== "") && (bos || bulunan > 0);

    async function kaydet() {
        setKaydediyor(true);
        setHata(null);
        try {
            if (ekleme) {
                await addMailListMembers(liste.mail_list_id, { filter: parametreler });
            } else {
                await createMailList({ name: ad.trim(), note: not.trim(), ...(bos ? {} : { filter: parametreler }) });
            }
            if (onDone) onDone();
        } catch (e) {
            setHata(e.message);
        } finally {
            setKaydediyor(false);
        }
    }

    return (
        <Modal isOpen toggle={toggle} size="xl" scrollable>
            <ModalHeader toggle={toggle}>{ekleme ? t("emailsListe.uyeEkleBaslik", { ad: liste.mail_list_name }) : t("emailsListe.yeniListe")}</ModalHeader>
            <ModalBody>
                <p className="text-muted">{t(ekleme ? "emailsListe.uyeEkleAciklama" : "emailsListe.yeniAciklama")}</p>
                {!ekleme && (
                    <div className="row g-3 mb-3">
                        <div className="col-md-5">
                            <Label className="form-label" htmlFor="liste-ad">{t("emailsListe.listeAdi")}</Label>
                            <Input id="liste-ad" value={ad} maxLength={150} onChange={(e) => setAd(e.target.value)} />
                        </div>
                        <div className="col-md-7">
                            <Label className="form-label" htmlFor="liste-not">{t("emailsListe.not")}</Label>
                            <Input id="liste-not" value={not} maxLength={500} onChange={(e) => setNot(e.target.value)} />
                        </div>
                        <div className="col-12">
                            <div className="form-check">
                                <Input id="liste-bos" type="checkbox" className="form-check-input" checked={bos} onChange={(e) => setBos(e.target.checked)} />
                                <Label className="form-check-label" htmlFor="liste-bos">{t("emailsListe.bosListe")}</Label>
                            </div>
                        </div>
                    </div>
                )}

                {!bos && (
                    <div>
                        <div className="mb-3">
                            <Label className="form-label" htmlFor="liste-arama">{t("emailsListe.arama")}</Label>
                            <Input id="liste-arama" type="search" value={arama} placeholder={t("emailsListe.aramaYeri")} onChange={(e) => setArama(e.target.value)} />
                        </div>
                        <KullaniciFiltreleri
                            filtre={filtre} onChange={setFiltre} roller={roller} fuarlar={fuarlar}
                            aktifSayi={aktifFiltreSayisi(filtre)} onTemizle={() => setFiltre(BOS_FILTRE)}
                        />

                        <div className="mt-4">
                            {!buSonuc && <div className="text-muted small"><Spinner size="sm" /> {t("emailsListe.sayiniyor")}</div>}
                            {buSonuc?.hata && <Alert color="danger">{buSonuc.hata}</Alert>}
                            {buSonuc?.veri && (
                                <div>
                                    <p className="mb-2">
                                        <strong>{t("emailsListe.bulunan", { n: buSonuc.veri.matched })}</strong>
                                        {" · "}{t("emailsListe.adresiYok", { n: buSonuc.veri.without_address })}
                                        {" · "}{t("emailsListe.pasifSayisi", { n: buSonuc.veri.inactive })}
                                    </p>
                                    {suzgecYok && <Alert color="warning" className="py-2">{t("emailsListe.suzgecYok")}</Alert>}
                                    {buSonuc.veri.sample?.length > 0 && (
                                        <Table size="sm" responsive className="mb-0">
                                            <tbody>
                                                {buSonuc.veri.sample.map((u) => (
                                                    <tr key={u.user_id}>
                                                        <td>{u.user_name} {u.user_surname}</td>
                                                        <td>{u.user_email || <span className="text-danger">{t("emailsListe.adresYok")}</span>}</td>
                                                        <td>{u.user_active ? "" : t("emailsListe.pasif")}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {hata && <Alert color="danger" className="mt-3 mb-0">{hata}</Alert>}
            </ModalBody>
            <ModalFooter>
                <Button color="primary" disabled={!kaydedebilir} onClick={kaydet}>
                    {kaydediyor ? <Spinner size="sm" /> : t(ekleme ? "emailsListe.ekle" : "emailsListe.olustur")}
                </Button>
                <Button color="light" onClick={toggle}>{t("common:iptal")}</Button>
            </ModalFooter>
        </Modal>
    );
};

const ListeOlustur = ({ isOpen, toggle, liste, onDone }) => (isOpen ? <OlusturForm toggle={toggle} liste={liste} onDone={onDone} /> : null);

export default ListeOlustur;
